import { useEffect } from "react";

interface UseKeyboardShortcutProps {
    key: string;
    modifiers?: ("ctrl" | "cmd" | "shift" | "alt")[];
    callback: () => void;
    enabled?: boolean;
}

export function useKeyboardShortcut({ key, modifiers = [], callback, enabled = true }: UseKeyboardShortcutProps) {
    useEffect(() => {
        if (!enabled) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key.toLowerCase() !== key.toLowerCase()) return;

            const hasCtrlOrCmd = modifiers.includes("ctrl") || modifiers.includes("cmd");
            if (hasCtrlOrCmd) {
                if (!(event.ctrlKey || event.metaKey)) return;
            }

            if (modifiers.includes("shift") && !event.shiftKey) return;
            if (modifiers.includes("alt") && !event.altKey) return;

            const target = event.target as HTMLElement;
            const isInInput =
                target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.contentEditable === "true";

            if (isInInput && !(hasCtrlOrCmd && key === "k")) {
                return;
            }

            event.preventDefault();
            callback();
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [key, modifiers, callback, enabled]);
}

export function useSearchShortcut(callback: () => void) {
    useKeyboardShortcut({
        key: "k",
        modifiers: ["ctrl"],
        callback,
    });

    useKeyboardShortcut({
        key: "k",
        modifiers: ["cmd"],
        callback,
    });
}

export function useNewChatShortcut(callback: () => void) {
    useKeyboardShortcut({
        key: "n",
        modifiers: ["ctrl", "alt"],
        callback,
    });

    useKeyboardShortcut({
        key: "n",
        modifiers: ["cmd", "alt"],
        callback,
    });
}

export function useHelpShortcut(callback: () => void) {
    useKeyboardShortcut({
        key: "?",
        modifiers: [],
        callback,
    });
}

export function useBugReportShortcut(callback: () => void) {
    useKeyboardShortcut({
        key: "F9",
        modifiers: [],
        callback,
    });
}
